import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  CreateSkuRuleRequest,
  SkuRule,
  SkuRuleApplyRequest,
  UpdateSkuRuleRequest,
} from '@nucleus-commerce/api-client/admin';

import { byRulePosition } from '../api/sku-rule-mappers';
import { skuRulesApi } from '../api/sku-rules-api';

import { dictionaryKeys } from './dictionary-keys';

/**
 * A rule change moves claims and the default flag, which product types carry as `skuRule` and
 * the product form reads through `resolve`; both are refetched along with the list.
 */
function remember(queryClient: QueryClient, row: SkuRule) {
  queryClient.setQueryData(dictionaryKeys.skuRule(row.id), row);
  queryClient.setQueryData<SkuRule[]>(dictionaryKeys.skuRules(), (rows) => {
    if (!rows) return rows;
    const index = rows.findIndex((r) => r.id === row.id);
    const next = index === -1 ? [...rows, row] : rows.with(index, row);
    return next
      .map((r) => (row.isDefault && r.id !== row.id ? { ...r, isDefault: false } : r))
      .sort(byRulePosition);
  });
  return settle(queryClient);
}

function settle(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: dictionaryKeys.productTypes() });
  void queryClient.invalidateQueries({ queryKey: [...dictionaryKeys.skuRules(), 'resolve'] });
  return queryClient.invalidateQueries({ queryKey: dictionaryKeys.skuRules(), exact: true });
}

/** 422 SKU_TEMPLATE_INVALID lands on the template fields; 409 PRODUCT_TYPE_CLAIMED on the picker. */
export function useCreateSkuRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSkuRuleRequest) => skuRulesApi.create(body),
    meta: { successMessage: 'skuRules:toasts.created', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

export function useUpdateSkuRule(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSkuRuleRequest) => skuRulesApi.update(id, body),
    meta: { successMessage: 'skuRules:toasts.updated', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

/** Products keep their style numbers and SKUs; only the claims go. */
export function useDeleteSkuRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => skuRulesApi.remove(id),
    meta: { successMessage: 'skuRules:toasts.deleted', silentError: true },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: dictionaryKeys.skuRule(id), exact: true });
      queryClient.setQueryData<SkuRule[]>(dictionaryKeys.skuRules(), (rows) =>
        rows?.filter((r) => r.id !== id),
      );
      return settle(queryClient);
    },
  });
}

/** Answers 202 with the run row; the dialog polls it. One run per rule at a time (409). */
export function useApplySkuRule(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SkuRuleApplyRequest) => skuRulesApi.apply(id, body),
    meta: { silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dictionaryKeys.skuRuleRuns(id) }),
  });
}
